import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { LangService } from "../../../../../services/lang.service";
import { DashLangService } from "../../../../../modules/home/dash/service/dash.lang.service";
import { StateService } from "../../../../../services/state.service";
import { SearchService } from "../../../../../services/search/search.service";
import { LoginStore } from "../../../../../stores/login.store";
import { S25LoadingInlineStaticComponent } from "../../../../../modules/s25-loading/s25.loading.inline.static.component";
import { S25IconComponent } from "../../../../../modules/s25-icon/s25.icon.component";
import { HomeDashCustomize, HomeDashPanelModel, HomeDashPanelTypeId } from "../home.dash.panel.model";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { s25Resource } from "../../../../../util/s25.resource";
import { Item } from "../../../../../pojo/Item";
import { HomeDashPanelListItemComponent } from "./home.dash.panel.list.item.component";
import { normalizeDashPanelListItem } from "./home.dash.panel.list.item.util";
import { DashPanelListItemBean } from "./home.dash.panel.list.item.types";
import { buildDashDitemDisplayLang } from "./home.dash.panel.list.ditem.format";
import { Search } from "../../../../../modules/s25-ql/search";

/** Dashboard list panels use base item type id + this offset for favorite-calendar deep links. */
const FAVORITES_CALENDAR_ITEM_TYPE_OFFSET = 100;

type ListLang = {
    style: Record<number, { panel?: string; body?: string; styleClass?: string }>;
    itemsnone: Record<number, { text?: string; style?: string; desc?: { style?: string; a?: string; b?: string } }>;
    tail: Record<number, { text?: string }>;
    itemsloading: string;
};

@TypeManagerDecorator("s25-ng-dash-panel-list")
@Component({
    selector: "s25-ng-dash-panel-list",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25LoadingInlineStaticComponent, S25IconComponent, HomeDashPanelListItemComponent],
    host: { class: "s25-ng-dash-panel-list dash-panel-list" },
    template: `
        @if (vm(); as data) {
            @if (showListBody()) {
                <div [attr.id]="data.mainstyle.body" [class]="data.bodyClassNames">
                    <ul class="s25-ditem-list">
                        @if (data.model.isLoading) {
                            <li>
                                <s25-ng-loading-inline-static [text]="data.loadingText" />
                            </li>
                        }
                        @if (!data.model.isLoading && data.model.items) {
                            @for (row of listItems(); track listItemTrackKey(row, rowIndex); let rowIndex = $index) {
                                <li>
                                    <s25-ng-dash-panel-list-item
                                        [item]="row"
                                        [loggedIn]="data.loggedIn"
                                        [ditemLang]="data.ditemLang"
                                    />
                                </li>
                            }
                        }
                        @if (!data.model.isLoading && !data.model.items && data.itemsnone) {
                            <li [class]="data.itemsnoneRowClass">
                                <div>{{ data.itemsnone.text }}</div>
                                @if (data.itemsnone.desc; as desc) {
                                    <div class="itemsnone-desc">
                                        <span>{{ desc.a }}</span>
                                        <div class="itemsnone-icon-col inline-block">
                                            <div [class]="itemsnoneIconClass(desc)" aria-hidden="true"></div>
                                        </div>
                                        <span>{{ desc.b }}</span>
                                    </div>
                                }
                            </li>
                        }
                    </ul>
                    @if (data.tail) {
                        @if (data.model.items?.length) {
                            <div class="favorite-cal-link">
                                <div
                                    class="s25-object pointer c-iconLabelLink"
                                    tabindex="0"
                                    role="button"
                                    (click)="tailNavigate(data.model)"
                                    (keydown.enter)="tailNavigate(data.model)"
                                    (keydown.space)="onTailSpace($event, data.model)"
                                    (mouseenter)="tailHover.set(true)"
                                    (mouseleave)="tailHover.set(false)"
                                >
                                    <span class="dh-col-a c-iconLabelLink__icon">
                                        <s25-ng-icon type="calendarCalendar" [ariaLabel]="false" />
                                    </span>
                                    <span class="c-iconLabelLink__label" [class.hover]="tailHover()">{{
                                        data.tail.text
                                    }}</span>
                                </div>
                            </div>
                        }
                    }
                </div>
            }
        }
    `,
})
export class HomeDashPanelListComponent {
    readonly model = input.required<HomeDashPanelModel>();
    readonly isToolbox = input(false);
    readonly customize = input<HomeDashCustomize | undefined>(undefined);
    /** Bumped by the panel orchestrator when list body should re-sync (loading state / items changed). */
    readonly refreshTick = input(0);

    readonly tailHover = signal(false);
    private readonly loginStore = inject(LoginStore);

    private readonly listBootstrap = s25Resource(async () => {
        const [lang, appLang] = await Promise.all([DashLangService.getLang(), LangService.getLang()]);
        return {
            listLang: lang.panel?.["list"] as ListLang,
            ditemLang: buildDashDitemDisplayLang(appLang as never),
        };
    });

    /**
     * Mirrors legacy `panel/list.tmpl`: `ng-if="!isToolbox && !model.isCollapsed"` on the body (plus
     * `uib-collapse` on that element). Parent bumps {@link refreshTick} when the head toggle mutates
     * `isCollapsed` so OnPush sees the change without replacing the `panel` input reference.
     */
    readonly showListBody = computed(() => {
        this.refreshTick();
        return !this.isToolbox() && !this.model().isCollapsed;
    });

    readonly vm = computed(() => {
        this.refreshTick();
        const seed = this.listBootstrap.value();
        const lang = seed?.listLang;
        const panelModel = this.model();
        if (!lang) {
            return undefined;
        }
        const loggedInUser = this.loginStore.loggedIn();
        const mainstyle = lang.style[panelModel.typeId as number] ?? {};
        const itemsnone = lang.itemsnone[panelModel.typeId as number];
        const tail = lang.tail[panelModel.typeId as number];
        const dynamicSuffix = `${mainstyle.styleClass ?? ""}${loggedInUser ? "" : " padding-right"}`.trim();
        const bodyClassNames = [
            "sectionBody",
            "dash-panel-body",
            "dash-panel-body--flush",
            "collapse-no-animation",
            dynamicSuffix,
        ]
            .filter(Boolean)
            .join(" ");
        const itemsnoneRowClass = itemsnone
            ? ["li-item", "textOnly", "dash-panel-body--padded", itemsnone.style].filter(Boolean).join(" ")
            : "";
        return {
            model: panelModel,
            mainstyle,
            itemsnone,
            tail,
            bodyClassNames,
            itemsnoneRowClass,
            loadingText: lang.itemsloading,
            loggedIn: loggedInUser,
            ditemLang: seed.ditemLang,
        };
    });

    readonly listItems = computed((): DashPanelListItemBean[] => {
        this.refreshTick();
        const panelModel = this.model();
        const rawItems = panelModel.items as DashPanelListItemBean[] | undefined;
        if (!rawItems?.length) {
            return [];
        }
        const panelTypeId = panelModel.typeId as number;
        return rawItems.map((item) => normalizeDashPanelListItem(item, panelTypeId));
    });

    async tailNavigate(panelModel: HomeDashPanelModel) {
        const panelTypeId = panelModel.typeId as number;
        if (panelTypeId === HomeDashPanelTypeId.FavoriteLocations) {
            const searchPerms = await SearchService.getSearchTypePerms();
            if (!searchPerms[Item.Ids.Location]?.simple) {
                return StateService.go(
                    "search",
                    {
                        viz: "availability",
                        subject: "event",
                        // Must match Search.predefined Event entry (singular space_favorite).
                        search: Search.eventsInStarredLocations,
                    },
                    { source: "state-service" },
                );
            }
        }
        return StateService.gotoItem({
            itemTypeId: panelTypeId + FAVORITES_CALENDAR_ITEM_TYPE_OFFSET,
            forceView: "availability",
        });
    }

    onTailSpace(event: Event, panelModel: HomeDashPanelModel): void {
        event.preventDefault();
        void this.tailNavigate(panelModel);
    }

    itemsnoneIconClass(desc: { style?: string }): string {
        return ["itemsnone-icon", "inline-block", desc.style?.trim()].filter(Boolean).join(" ");
    }

    listItemTrackKey(row: DashPanelListItemBean, rowIndex: number): string {
        return `${row.itemTypeId ?? 0}-${row.itemId ?? rowIndex}`;
    }
}
